'use client'

import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { Button } from '@acko/button'
import { Dialog } from '@acko/dialog'
import { Drawer } from '@acko/drawer'
import { Typography } from '@acko/typography'
import { getConfig } from '@configs/envConfig'
import getJourneyRedirect from '@components/common/IllogicalFlowModal/proposalFetchAndRedirect'
import { config, segmentModalType } from '@components/common/IllogicalFlowModal/config'
import {
  getModalType,
  getReverseAutoProduct,
  getSegmentPayload,
  redirectUrlWithQuery,
} from '@components/common/IllogicalFlowModal/util'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import { getR2d2Payload } from '@utils/r2d2Payload'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AutoProduct } from 'src/enums/AutoProduct'
import { bikeProposalAPIUrl } from './useBikeRegistrationFlow'

interface SimpleBikeFlowModalProps {
  errorCode?: string
  illogicalFlowData?: any
  onClose: () => void
  onEditRegistration: () => void
  product: AutoProduct
  regNo: string
  setProposalAPIResponse: (response: any) => void
  setShowIllogicalFlowModal: (show: boolean) => void
  show: boolean
}

const trackModalEvent = async (eventName: string, eventProps: object) => {
  try {
    await trackEvent(eventName, eventProps)
  } catch {
    // Analytics should not block the modal action.
  }
}

export const SimpleBikeFlowModal = ({
  errorCode = '',
  illogicalFlowData = {},
  onClose,
  onEditRegistration,
  product,
  regNo,
  setProposalAPIResponse,
  setShowIllogicalFlowModal,
  show,
}: SimpleBikeFlowModalProps) => {
  const [isFetching, setIsFetching] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const envConfig = useEnvVariablesClientConfig()
  const turnstileConfig = getConfig(envConfig.ACKO_ENV)?.turnstile

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const isVehicleMismatchModal = errorCode === 'ILLOGICAL_FLOW_UNACCEPTABLE_ASSET'
  const identifiedVehicleType = illogicalFlowData?.vehicle_type || getReverseAutoProduct(product)
  const modalType = getModalType(errorCode, illogicalFlowData, product)
  const modalData = config[modalType]
  const isSingleCtaCase = modalData?.secondaryCtaText === null
  const { user_logged_in: userLoggedIn, last_digits: lastDigits, redirect_url: redirectUrl } = illogicalFlowData

  const illogicalFlowAdditionalSegmentEventProps = useMemo(
    () => ({
      mobile_verified: userLoggedIn,
      context: segmentModalType[modalType],
      mobileLastDigits: lastDigits,
      cc: illogicalFlowData?.cc,
      fuelType: illogicalFlowData?.fuel_type,
      is_renewal: illogicalFlowData?.is_renewal,
      vehicle_type: illogicalFlowData?.vehicle_type,
    }),
    [illogicalFlowData, lastDigits, modalType, userLoggedIn]
  )

  const getSegmentEventProps = useCallback(
    (vehicleMismatchModal, ekind) => {
      const segmentEventProps = {
        registration_number: regNo,
        product,
        ...getSegmentPayload(product),
        ...getR2d2Payload({
          ekind,
          edata: {
            registration_number: illogicalFlowData?.registration_number,
            isLoggedIn: userLoggedIn,
            context: segmentModalType[modalType],
          },
          odata: { product },
        }),
      }

      if (vehicleMismatchModal) return segmentEventProps
      return {
        ...segmentEventProps,
        ...illogicalFlowAdditionalSegmentEventProps,
      }
    },
    [illogicalFlowAdditionalSegmentEventProps, illogicalFlowData, modalType, product, regNo, userLoggedIn]
  )

  const redirectToJourney = () => {
    window.location.href = !!redirectUrl && userLoggedIn ? redirectUrlWithQuery(redirectUrl) : '/login?next=/myaccount'
  }

  useEffect(() => {
    if (!show) return

    const eventName = isVehicleMismatchModal
      ? `${identifiedVehicleType}_detected_modal_loaded`
      : 'policy_already_exists_modal_shown'
    const ekind = isVehicleMismatchModal
      ? `${identifiedVehicleType}_detected_modal_shown`
      : 'policy_already_exists_modal_shown'

    void trackModalEvent(eventName, getSegmentEventProps(isVehicleMismatchModal, ekind))
  }, [getSegmentEventProps, identifiedVehicleType, isVehicleMismatchModal, show])

  const heading = isVehicleMismatchModal
    ? `Uh-oh! Looks like this number belongs to a ${identifiedVehicleType}`
    : modalData?.heading
  const subHeading = isVehicleMismatchModal
    ? 'How would you like to continue?'
    : userLoggedIn || isSingleCtaCase
      ? modalData?.subHeading
      : modalData?.notLoggedSubHeading(lastDigits)
  const icon = {
    src: illogicalFlowData?.vehicle_image ?? illogicalFlowData?.logo_url ?? modalData?.iconSrc,
    alt: modalData?.iconAlt,
  }
  const primaryCtaText = isVehicleMismatchModal
    ? `Buy ${identifiedVehicleType} insurance`
    : userLoggedIn || isSingleCtaCase
      ? modalData?.primaryCtaText
      : 'Log in'
  const secondaryCtaText = isVehicleMismatchModal ? 'Edit registration number' : modalData?.secondaryCtaText

  const handlePrimaryClick = async () => {
    if (isVehicleMismatchModal) {
      await trackModalEvent(
        `tap_btn_${identifiedVehicleType}_detected_modal_buy_${identifiedVehicleType}_insurance`,
        getSegmentEventProps(isVehicleMismatchModal, `${identifiedVehicleType}_redirection_cta_clicked`)
      )
      await getJourneyRedirect(
        regNo,
        getReverseAutoProduct(product),
        bikeProposalAPIUrl,
        setIsFetching,
        setShowIllogicalFlowModal,
        setProposalAPIResponse,
        turnstileConfig
      )
      return
    }

    const eventName = userLoggedIn
      ? 'tap_btn_policy_already_exists_modal_view_policy'
      : 'tap_btn_policy_already_exists_modal_login'
    await trackModalEvent(eventName, getSegmentEventProps(isVehicleMismatchModal, eventName))

    if (isSingleCtaCase) {
      onClose()
      return
    }

    redirectToJourney()
  }

  const handleSecondaryClick = async () => {
    const eventName = isVehicleMismatchModal
      ? `tap_btn_${identifiedVehicleType}_detected_modal_edit_reg_no`
      : 'tap_btn_policy_already_exists_modal_edit_reg_no'
    const ekind = isVehicleMismatchModal
      ? `edit_reg_on_${identifiedVehicleType}_modal_clicked`
      : 'tap_btn_policy_already_exists_modal_edit_reg_no'

    await trackModalEvent(eventName, getSegmentEventProps(isVehicleMismatchModal, ekind))
    onEditRegistration()
  }

  const handleClose = async () => {
    const eventName = isVehicleMismatchModal
      ? `tap_btn_${identifiedVehicleType}_detected_modal_cross`
      : 'tap_btn_policy_already_exists_modal_cross'

    await trackModalEvent(eventName, getSegmentEventProps(isVehicleMismatchModal, eventName))
    onClose()
  }

  const content = (
    <div className="flex flex-col gap-16 items-center text-center">
      {icon?.src ? (
        <div className="w-56 h-56 md:w-80 md:h-80 rounded-[var(--radius-2xl)] md:rounded-[var(--radius-3xl)] bg-[var(--color-accent-grey-surface)] overflow-hidden flex items-center justify-center shrink-0">
          <Image alt={icon.alt || ''} className="w-full h-full object-contain" height={80} src={icon.src} width={80} />
        </div>
      ) : null}
      <p className="![font-size:var(--font-body-md-size)] ![line-height:var(--font-body-md-line)] ![font-weight:var(--font-weight-semibold)] text-[var(--color-text-primary)]">
        {regNo}
      </p>
      <div className="flex flex-col gap-8 items-center">
        <Typography scale="xl" emphasis="medium" as="h2" color="primary">
          {heading}
        </Typography>
        {subHeading ? (
          <Typography scale="base" color="secondary" as="p">
            {subHeading}
          </Typography>
        ) : null}
      </div>
      <div className="flex flex-col md:flex-row w-full gap-16">
        {secondaryCtaText ? (
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="w-full md:flex-1 md:w-auto min-w-0 order-2 md:order-1"
            onClick={() => {
              void handleSecondaryClick()
            }}>
            {secondaryCtaText}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="primary"
          size="lg"
          className="w-full md:flex-1 md:w-auto min-w-0 order-1 md:order-2"
          loading={isFetching}
          disabled={isFetching}
          onClick={() => {
            void handlePrimaryClick()
          }}>
          {primaryCtaText}
        </Button>
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <Drawer
        open={show}
        onClose={() => {
          void handleClose()
        }}
        side="bottom"
        className="!max-h-none [&_.acko-drawer-header]:justify-end [&_.acko-drawer-header]:[border-bottom:none]">
        {content}
      </Drawer>
    )
  }

  return (
    <Dialog
      open={show}
      onClose={() => {
        void handleClose()
      }}
      className="!w-full [&_.acko-dialog-header]:justify-end">
      {content}
    </Dialog>
  )
}
